import {
  Box,
  ButtonSkin,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  TextButton,
} from '@wix/design-system';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContextProvider } from '../../AppLoader';
import { ButtonWithLoader } from '../../ButtonWithLoader/ButtonWithLoader';
import { DialogHeader } from '../../DialogHeader/DialogHeader';
import { MainHeader } from '../../MainHeader';
import { ThemedText } from '../../ThemedComponents';
import { dataHooks } from '../../../dataHooks';
import { ArrowLeft, ArrowRight } from '@wix/wix-ui-icons-common';
import PasskeyEnrollIllustration from '../../../assets/images/passkeyEnrollIllustration.svg';
import PasskeySuccessIllustration from '../../../assets/images/passkeySuccessIllustration.svg';
import PasskeyIcon from '../../../assets/images/passkeyIcon.svg';
import SsoIcon from '../../../assets/images/ssoIcon.svg';
import { useIsRtl } from '../../../hooks';
import {
  passkeyConnectionsSucceedSrc5Evid5022,
  postLoginErrorSrc5Evid6002,
} from '@wix/bi-logger-identity-data/v2';
import { PASSKEY_ENROLL_KEYS } from './keys';
import { PasskeyEnrollError } from './PasskeyEnrollError';
import type { PasskeyEnrollmentStatus } from '../../../stores/passkeyEnroll';
import { POST_LOGIN_OFFERS } from '../../../utils/constants';

interface EnrollButtonProps {
  enrollmentStatus: PasskeyEnrollmentStatus;
  buttonText: string;
  skin: ButtonSkin;
  showArrowOnOver: boolean;
  showLoader: boolean;
  onClick: () => void;
}

const EnrollButton: React.FC<EnrollButtonProps> = ({
  enrollmentStatus,
  buttonText,
  skin,
  showArrowOnOver,
  showLoader,
  onClick,
}) => {
  return (
    <Box
      width="fit-content"
      minWidth={enrollmentStatus === 'success' ? undefined : '200px'}
    >
      <ButtonWithLoader
        dataHook={dataHooks.postLogin.passkeyEnroll.button}
        priority="primary"
        skin={skin}
        prefixIcon={
          enrollmentStatus !== 'enrolling' && enrollmentStatus !== 'success' ? (
            <PasskeyIcon />
          ) : undefined
        }
        showArrowOnOver={showArrowOnOver}
        onClick={onClick}
        showLoader={showLoader}
      >
        {buttonText}
      </ButtonWithLoader>
    </Box>
  );
};

const MfaHint: React.FC<{ hint: string }> = ({ hint }) => (
  <Box
    direction="horizontal"
    gap={1}
    align="center"
    verticalAlign="middle"
    dataHook={dataHooks.postLogin.passkeyEnroll.mfaHint}
  >
    <SsoIcon />
    <ThemedText size="tiny" weight="thin" secondary>
      {hint}
    </ThemedText>
  </Box>
);

const PostLoginPasskeyEnroll: React.FC = observer(() => {
  const { rootStore } = useContext(AppContextProvider);
  const { postLoginStore, passkeyEnrollStore, displayStore, biLogger } =
    rootStore;
  const { preset } = displayStore;
  const { t } = useTranslation();
  const isRtl = useIsRtl();
  const [isProceedingToPostAuth, setIsProceedingToPostAuth] = useState(false);

  useEffect(() => {
    if (postLoginStore.postLoginOffer?.offerGuid) {
      rootStore.dealerApi.sendDealerViewEvent(
        postLoginStore.postLoginOffer.offerGuid,
      );
    }
  }, []);

  const enrollmentStatus = passkeyEnrollStore.enrollmentStatus;
  const offerId =
    postLoginStore.postLoginOffer?.asset?.creative?.identifier ?? '';
  const isMfaOffer = offerId === POST_LOGIN_OFFERS.PASSKEY_ENROLL_MFA;

  const { common, general, mfa, success } = PASSKEY_ENROLL_KEYS;
  const getTitleKey = (): string => {
    if (enrollmentStatus === 'success') {
      return success.title;
    }
    if (isMfaOffer) {
      return mfa.title;
    }
    return general.title;
  };
  const titleKey = getTitleKey();
  const subtitleKey =
    enrollmentStatus === 'success' ? success.subtitle : common.subtitle;
  const buttonKey =
    enrollmentStatus === 'success' ? success.button : common.button;

  const handleButtonClick = async () => {
    if (enrollmentStatus === 'success') {
      setIsProceedingToPostAuth(true);
      postLoginStore.offerSucceed();
      return;
    }
    if (enrollmentStatus === 'enrolling') {
      return;
    }
    if (enrollmentStatus === 'error') {
      passkeyEnrollStore.reset();
    }
    const result = await passkeyEnrollStore.enrollPasskey();
    if (result.success) {
      biLogger.report(
        passkeyConnectionsSucceedSrc5Evid5022({
          origin: isMfaOffer
            ? 'post login - passkey - tired of code'
            : 'post login - passkey - login faster',
        }),
      );
    } else {
      biLogger.report(
        postLoginErrorSrc5Evid6002({
          post_login_offer: 'passkey',
          flow: isMfaOffer
            ? 'tired of code - 2fa with code'
            : 'login faster - no 2fa with code',
          error_name: result.errorCode,
        }),
      );
    }
  };

  const enrollmentLayout = (
    <MarketingPageLayout
      horizontalSize="medium"
      verticalSize="medium"
      content={
        <MarketingPageLayoutContent
          title={
            <DialogHeader.Title flow="authPage">
              {t(titleKey)}
            </DialogHeader.Title>
          }
          subtitle={
            <DialogHeader.SubTitleSmall>
              {t(subtitleKey)}
            </DialogHeader.SubTitleSmall>
          }
          actions={
            <Box direction="vertical" gap={4} align="left">
              <EnrollButton
                enrollmentStatus={enrollmentStatus}
                buttonText={t(buttonKey)}
                skin={preset.buttonDesign?.skin ?? 'standard'}
                showArrowOnOver={
                  preset.buttonDesign?.showArrowOnButton ?? false
                }
                showLoader={
                  enrollmentStatus === 'enrolling' || isProceedingToPostAuth
                }
                onClick={handleButtonClick}
              />
              {isMfaOffer && enrollmentStatus !== 'success' && (
                <MfaHint hint={t(mfa.hint)} />
              )}
              {enrollmentStatus === 'error' && passkeyEnrollStore.errorCode && (
                <PasskeyEnrollError
                  errorCode={passkeyEnrollStore.errorCode}
                  onClose={() => passkeyEnrollStore.reset()}
                />
              )}
            </Box>
          }
        />
      }
      image={
        <Box width="100%" align="right" verticalAlign="middle">
          {enrollmentStatus === 'success' ? (
            <PasskeySuccessIllustration />
          ) : (
            <PasskeyEnrollIllustration />
          )}
        </Box>
      }
    />
  );

  return (
    <Box
      direction="vertical"
      width="100%"
      minHeight="100vh"
      align="center"
      backgroundColor="WHITE"
    >
      <MainHeader
        showBackButton={false}
        disableLogoClick={false}
        headerAction={
          enrollmentStatus !== 'success' ? (
            <TextButton
              dataHook={dataHooks.postLogin.common.skip}
              size="tiny"
              weight="normal"
              skin="dark"
              underline="none"
              suffixIcon={
                isRtl ? <ArrowLeft size={18} /> : <ArrowRight size={18} />
              }
              onClick={() => postLoginStore.showLater()}
            >
              {t(PASSKEY_ENROLL_KEYS.header.skipCta)}
            </TextButton>
          ) : undefined
        }
      />
      <Box direction="vertical" verticalAlign="middle" style={{ flex: 1 }}>
        {enrollmentLayout}
      </Box>
    </Box>
  );
});

export default PostLoginPasskeyEnroll;
